export type IfcProfile = 'lite_minimal' | 'optimized_no_evac' | 'full_with_evac';

export type IfcVersionStatus = 'missing' | 'generating' | 'ready' | 'failed';

export type IfcDurationKind = 'actual' | 'estimated';

export interface IfcVersionItem {
  profile: IfcProfile;
  name: string;
  ownerId: string;
  extension: string;
  createTime: string | null;
  isDefault: boolean;
  exists: boolean;
  status: IfcVersionStatus;
  downloadUrl: string | null;
  generateUrl: string;
  durationSeconds: number | null;
  durationKind?: IfcDurationKind | null;
  durationLabel?: string | null;
  estimatedSeconds?: number | null;
  estimatedRangeSeconds?: [number, number] | null;
  estimatedRangeLabel?: string | null;
  isLongRunning?: boolean;
}

export const IFC_PROFILE_OPTIONS: { label: string; value: IfcProfile }[] = [
  { label: '轻量版', value: 'lite_minimal' },
  { label: '优化版', value: 'optimized_no_evac' },
  { label: '全量版', value: 'full_with_evac' },
];

export const IFC_PROFILE_LABELS: Record<IfcProfile, string> = {
  lite_minimal: '轻量版',
  optimized_no_evac: '优化版',
  full_with_evac: '全量版',
};

export const IFC_PROFILE_DESCRIPTIONS: Record<IfcProfile, string> = {
  lite_minimal: '最快生成版本',
  optimized_no_evac: '默认版本，含墙体优化',
  full_with_evac: '含疏散路径生成与展示',
};

export const IFC_STATUS_LABELS: Record<IfcVersionStatus, string> = {
  missing: '未生成',
  generating: '生成中',
  ready: '可下载',
  failed: '生成失败',
};

export const IFC_STATUS_TAG_COLORS: Record<IfcVersionStatus, string> = {
  missing: 'default',
  generating: 'processing',
  ready: 'success',
  failed: 'error',
};

export function hasGeneratingIfcVersion(versions: IfcVersionItem[]) {
  return versions.some((version) => version.status === 'generating');
}

export function getPreferredIfcProfile(
  versions: IfcVersionItem[],
  currentProfile: IfcProfile | null,
): IfcProfile | null {
  if (!versions.length) {
    return null;
  }

  if (currentProfile) {
    const currentVersion = versions.find((version) => version.profile === currentProfile);
    if (currentVersion?.status === 'ready') {
      return currentProfile;
    }
  }

  const readyVersions = versions.filter((version) => version.status === 'ready');
  if (!readyVersions.length) {
    return versions.find((version) => version.isDefault)?.profile
      ?? versions.find((version) => version.profile === 'optimized_no_evac')?.profile
      ?? versions[0].profile;
  }

  const allReady = readyVersions.length === versions.length;
  if (allReady) {
    const optimizedVersion = readyVersions.find((version) => version.profile === 'optimized_no_evac');
    if (optimizedVersion) {
      return optimizedVersion.profile;
    }
  }

  return readyVersions.find((version) => version.isDefault)?.profile
    ?? readyVersions[0].profile;
}

export function getIfcVersionByProfile(
  versions: IfcVersionItem[],
  profile: IfcProfile | null,
) {
  if (!profile) {
    return null;
  }
  return versions.find((version) => version.profile === profile) ?? null;
}

export function getIfcVersionFileName(spaceId: number, version: IfcVersionItem) {
  const baseName = version.name?.trim() || `model-${spaceId}-${version.profile}`;
  const extension = version.extension?.trim() || '.ifc';
  return `${baseName}${extension.startsWith('.') ? extension : `.${extension}`}`;
}

export function formatIfcDuration(durationSeconds: number | null | undefined) {
  if (durationSeconds == null) {
    return '--';
  }

  const normalizedSeconds = Math.max(0, Math.round(durationSeconds));
  if (normalizedSeconds < 60) {
    return `${normalizedSeconds}秒`;
  }

  const hours = Math.floor(normalizedSeconds / 3600);
  const minutes = Math.floor((normalizedSeconds % 3600) / 60);
  const seconds = normalizedSeconds % 60;
  if (hours) {
    return [
      `${hours}小时`,
      minutes ? `${minutes}分` : '',
      seconds ? `${seconds}秒` : '',
    ].filter(Boolean).join('');
  }

  return seconds ? `${minutes}分${seconds}秒` : `${minutes}分钟`;
}

export function getIfcDurationDisplay(version: IfcVersionItem) {
  if (version.durationLabel) {
    return version.durationLabel;
  }

  return `生成耗时：${formatIfcDuration(version.durationSeconds)}`;
}

function getDurationRangeLabelBySeconds(seconds: number) {
  if (seconds <= 30) {
    return '0-30秒';
  }
  if (seconds <= 60) {
    return '30-60秒';
  }
  if (seconds <= 120) {
    return '1-2分钟';
  }
  if (seconds <= 180) {
    return '2-3分钟';
  }
  if (seconds <= 300) {
    return '3-5分钟';
  }
  if (seconds <= 600) {
    return '5-10分钟';
  }
  return '10分钟以上';
}

export function getIfcRegenerateDurationLabel(version: IfcVersionItem | null) {
  if (!version) {
    return null;
  }

  if (version.estimatedRangeLabel) {
    return version.estimatedRangeLabel;
  }

  if (version.estimatedRangeSeconds?.length === 2) {
    const upperSeconds = Math.max(...version.estimatedRangeSeconds);
    return getDurationRangeLabelBySeconds(upperSeconds);
  }

  const referenceSeconds = version.estimatedSeconds ?? version.durationSeconds;
  if (referenceSeconds == null) {
    return null;
  }

  return getDurationRangeLabelBySeconds(Math.max(0, Math.round(referenceSeconds)));
}

export function canUseIfcVersionFile(version: IfcVersionItem | null): version is IfcVersionItem {
  return Boolean(version && version.status === 'ready' && version.exists);
}

export function getIfcEmptyMessage(version: IfcVersionItem | null) {
  if (!version) {
    return '暂无 IFC 版本信息';
  }

  switch (version.status) {
    case 'missing':
      return '当前版本尚未生成';
    case 'generating':
      return '当前版本正在生成中';
    case 'failed':
      return '当前版本生成失败，请重试';
    default:
      return '当前版本暂无可预览模型';
  }
}
